// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
// Components
import { EditableMarkdown } from "Components";
// Contexts
import { CaseContext, AuthContext, TechnologyContext } from "Stores";
import { WebsocketContext } from "Providers";
// Types
import { TNotificationMessageDTO, TRequirementDTO, TTechnologyCaseRequirementsDTO, TTechnologyRequirementScoreDTO } from "Types";
// styles
import styles from "./editableTechnologyRequirement.module.scss";
// Controllers
import { RequirementControllerSingleton } from "Controllers";
// Custom hooks
import { useDebounce } from "Hooks";
// Interfaces
import { ISetTechnologyCountsAction } from "Interfaces";
// Enums
import { TechnologyActionTypeEnum, ToastTypeEnum } from "Enums";
// Constants
import { HubConstants } from "Constants";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TEditableTechnologyRequirementScoreProps = {
    showRequirementName: boolean,
    requirement: TTechnologyCaseRequirementsDTO | TRequirementDTO,
    score: TTechnologyRequirementScoreDTO | undefined,
    isRequirementTable?: boolean,
    technologyId: number,
    originalTechnologyId: number
};

export const EditableTechnologyRequirementScore: FC<TEditableTechnologyRequirementScoreProps> = ({showRequirementName, requirement, score, isRequirementTable, technologyId, originalTechnologyId}: TEditableTechnologyRequirementScoreProps) => {
    // State
    const [editedScore, setEditedScore] = useState<string | undefined>(undefined);
    const [isOnlyWhitespace, setIsOnlyWhitespace] = useState<boolean>(score && score.score ? score.score.trim().length === 0 : true);

    // Let
    const debouncedEditedScore = useDebounce(editedScore, 500);
    const technologyRequirementScoreResourceName = score ? `${HubConstants.TECHNOLOGY_REQUIREMENT_SCORE_RESOURCE_NAME_PREFIX}${technologyId}${score.requirementId}` : "";

    // Contexts
    const { technologyState, technologyDispatch } = useContext(TechnologyContext);
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { webSocketController } = useContext(WebsocketContext);
    
    // Logic
    const updateRequirementScore = useCallback(async (text: string) => {
        // safety-checks
        if(!technologyId || text === null || text === undefined || editedScore === null || editedScore === undefined) return;
        
        setEditedScore(undefined);

        const isSuccess: boolean = await RequirementControllerSingleton.updateRequirementScore(requirement.id, technologyId, text, true);
        
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update requirement score.");
            return;
        }
    }, [editedScore, requirement.id, technologyId]);

    useEffect(() => {
        // do not trigger edit at first render
        if (debouncedEditedScore === null || debouncedEditedScore === undefined) return;
        // send new requirement score to the server
        updateRequirementScore(debouncedEditedScore);
    }, [debouncedEditedScore, updateRequirementScore]);

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    const handleRequirementScoreUpdated = useCallback((newRequirementScore: TTechnologyRequirementScoreDTO ) => {
        if (newRequirementScore.score === null || newRequirementScore.score === undefined) return;
        
        if (score && score.score) {
            score.score = newRequirementScore.score;
        }

        const isNewValueOnlyWhitespace = newRequirementScore.score.trim().length === 0;

        if(isOnlyWhitespace !== isNewValueOnlyWhitespace) {
            const newCounts = technologyState.counts;

            if(isNewValueOnlyWhitespace) {
                newCounts.requirementCount--;
                setIsOnlyWhitespace(true);
            } else {
                newCounts.requirementCount++;
                setIsOnlyWhitespace(false);
            }

            technologyDispatch({type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: newCounts} as ISetTechnologyCountsAction);
        }
    }, [isOnlyWhitespace, score, technologyDispatch, technologyState.counts]);
    
    // manage web sockets related events
    useEffect(() => {
        // safety-check
        if (!technologyRequirementScoreResourceName) return;
        
        // we add two handlers to listen on update and notification messages related to technology requirement resource name
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${technologyRequirementScoreResourceName}`, 
            handleRequirementScoreUpdated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyRequirementScoreResourceName}`, 
            handleNotificationMessages);
        
        // remove handlers on clean up time
        return () => {
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${technologyRequirementScoreResourceName}`, 
                handleRequirementScoreUpdated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${technologyRequirementScoreResourceName}`, 
                handleNotificationMessages);
        };
    }, [handleNotificationMessages, handleRequirementScoreUpdated, technologyRequirementScoreResourceName, webSocketController]);

    return (
        <div className={[styles.technologyRequirement, isRequirementTable ? styles.requirementsTable : ""].join(" ")} key={requirement.id}>
            {showRequirementName ? <div className={styles.requirementTitle}>{requirement.name}</div> : null}
            <div className={styles.requirementValue}>
                <EditableMarkdown
                    source={score && score.score ? score.score : ""}
                    entityId={`${technologyId}-${requirement.id}`}
                    noSource={"N/A"}
                    isEditable={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery} 
                    handleSourceEdited={setEditedScore}
                    env={technologyId ? {"technologyId": technologyId} : undefined}
                    extraClassNameWisiwygBottomContainer={styles.inTechnologyRequirementContainer}
                    resourceName={`requirementScore-${originalTechnologyId}-${requirement.id}`}/>
            </div>
        </div>
    );
};